import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AUTH_TOKEN_KEY,
  ApiRequestError,
  fetchAdminTeamInfo,
  fetchSystemUserInfo,
  formatInvoiceTaxLabel,
  canJoinAnotherTeam,
  getUserTeams,
  isUserInTeam,
  needsTeamPrompt,
  isSelectedTeamAdmin,
  canViewOrderTeamPrivacy,
  readTeamInfoId,
  resolveDefaultUserTeam,
  resolveUserOnlineStatus,
  showApiResultModal,
  type SystemUserInfo,
  type UserTeamMembership,
} from '@/api';
import NoTeamPrompt from '@/components/team/NoTeamPrompt';
import {
  CALENDAR_STATUS_META,
  getOrderStatusMeta,
  activeCalendarStatuses,
  createEmptyCalendarStatusCounts,
  hasCalendarStatusCounts,
  type CalendarDayStatusCounts,
} from '@/constants/calendarStatus';
import { ORDER_LIST_ACTION_CLASS } from '@/constants/orderUi';
import {
  canTeamAdminEditOrderBeforeComplete,
  canTeamAdminEditWorkHours,
  canUserMarkServiceCompleted,
  orderHasAssignedStaff,
  isAssignedServiceFullyCompleted,
  fetchCalendarDaily,
  fetchDailyOrders,
  type CalendarCompletedAmounts,
  formatOrderDurationHours,
  formatOrderTotalPrice,
  patchOrderAfterAssignment,
  patchOrderAfterOperation,
  patchOrderAfterServiceCompleted,
  patchOrderAfterStatusChange,
  OrderCloseAfterPaymentError,
  OrderClosedBeforePaymentError,
  postAdminReorder,
  postAssignOrder,
  postConfirmInvoicePaymentAndClose,
  postMarkServiceCompleted,
  postOrderBillingAdjustment,
  postSetOnlineStatus,
  resolveInvoiceCloseAction,
} from '@/api/modules/order';
import type { Order, OrderServicerInfo } from '@/types/order';
import FormattedServiceTime from '@/components/FormattedServiceTime';
import StaffScheduleCalendar from '@/pages/team/components/StaffScheduleCalendar';
import AssignOrderModal from '@/pages/orders/components/AssignOrderModal';
import { orderListPaymentLabel } from '@/pages/orders/components/OrderCard';
import InvoiceOrderModal from '@/pages/orders/components/InvoiceOrderModal';
import CreateRecurringOrderModal from '@/pages/orders/components/CreateRecurringOrderModal';
import ReorderOrderModal, { type ReorderOrderConfirmPayload } from '@/pages/orders/components/ReorderOrderModal';
import ConfirmPaymentCloseModal, {
  invoiceCloseActionLabel,
} from '@/pages/orders/components/ConfirmPaymentCloseModal';
import { rememberInvoiceSent } from '@/lib/invoiceSentOrders';
import type { OrderBillingValues } from '@/lib/orderBilling';
import { formatGstPercentInput, orderGstRateToDecimal } from '@/lib/orderPricing';

function todayYmd(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDayLabel(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return ymd;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return ymd;
  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export default function TeamStatsPage() {
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [userTeams, setUserTeams] = useState<UserTeamMembership[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [teamInfoLoading, setTeamInfoLoading] = useState(true);
  const [inTeam, setInTeam] = useState(false);
  const [calendarDate, setCalendarDate] = useState(todayYmd);
  const [calendarMonth, setCalendarMonth] = useState(() => todayYmd().slice(0, 7));
  const [calendarStatusCounts, setCalendarStatusCounts] = useState<CalendarDayStatusCounts>(
    createEmptyCalendarStatusCounts,
  );
  const [dayStatusMap, setDayStatusMap] = useState<Record<string, CalendarDayStatusCounts>>({});
  const [dayAmounts, setDayAmounts] = useState<Record<string, CalendarCompletedAmounts>>({});
  const [monthAmounts, setMonthAmounts] = useState<CalendarCompletedAmounts | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [dayOrders, setDayOrders] = useState<Order[]>([]);
  const [dayOrdersLoading, setDayOrdersLoading] = useState(false);
  const [dayOrdersError, setDayOrdersError] = useState<string | null>(null);
  const [scheduleTick, setScheduleTick] = useState(0);
  const [assignOrder, setAssignOrder] = useState<Order | null>(null);
  const [assignMode, setAssignMode] = useState<'assign' | 'billing'>('billing');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [completingOrderId, setCompletingOrderId] = useState<string | null>(null);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [recurringOrder, setRecurringOrder] = useState<Order | null>(null);
  const [reorderOrder, setReorderOrder] = useState<Order | null>(null);
  const [reorderSubmitting, setReorderSubmitting] = useState(false);
  const [createdTeamTaxLabel, setCreatedTeamTaxLabel] = useState<string | null>(null);
  const [createdTeamId, setCreatedTeamId] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);
  const [onlineStatusUpdating, setOnlineStatusUpdating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const applyProfileTeams = useCallback((userProfile: SystemUserInfo) => {
    setProfile(userProfile);
    setIsOnline(resolveUserOnlineStatus(userProfile));
    const teams = getUserTeams(userProfile);
    setUserTeams(teams);
    setInTeam(isUserInTeam(userProfile) || teams.length > 0);
    return teams;
  }, []);

  const selectedTeam = useMemo(() => {
    if (userTeams.length === 0) return null;
    if (selectedTeamId) {
      const matched = userTeams.find((t) => String(t.teamId) === selectedTeamId);
      if (matched) return matched;
    }
    return userTeams[0] ?? null;
  }, [userTeams, selectedTeamId]);

  const isTeamCreator = isSelectedTeamAdmin(selectedTeam);

  useEffect(() => {
    const focusDate = (location.state as { focusDate?: string } | null)?.focusDate;
    if (!focusDate || !/^\d{4}-\d{2}-\d{2}$/.test(focusDate)) return;
    setCalendarDate(focusDate);
    setCalendarMonth(focusDate.slice(0, 7));
    setScheduleTick((n) => n + 1);
  }, [location.state]);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setProfile(null);
      setUserTeams([]);
      setSelectedTeamId(null);
      setInTeam(false);
      setTeamInfoLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((userProfile) => {
        if (cancelled) return;
        applyProfileTeams(userProfile);
        const defaultTeam = resolveDefaultUserTeam(userProfile);
        setSelectedTeamId(defaultTeam ? String(defaultTeam.teamId) : null);
      })
      .catch(() => {
        if (!cancelled) {
          setProfile(null);
          setUserTeams([]);
          setSelectedTeamId(null);
          setInTeam(false);
        }
      })
      .finally(() => {
        if (!cancelled) setTeamInfoLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyProfileTeams]);

  useEffect(() => {
    if (!inTeam || !selectedTeamId) {
      setCalendarStatusCounts(createEmptyCalendarStatusCounts());
      setDayStatusMap({});
      setDayAmounts({});
      setMonthAmounts(null);
      setCalendarError(null);
      setCalendarLoading(false);
      setDayOrders([]);
      setDayOrdersError(null);
      setDayOrdersLoading(false);
      return;
    }

    let cancelled = false;
    setCalendarLoading(true);
    setCalendarError(null);
    fetchCalendarDaily(calendarDate, calendarMonth, isTeamCreator)
      .then((result) => {
        if (cancelled) return;
        setCalendarStatusCounts(result.statusCounts);
        setDayStatusMap((prev) => ({ ...prev, ...result.days }));
        setDayAmounts((prev) => ({ ...prev, ...result.dayAmounts }));
        setMonthAmounts(result.monthAmounts);
      })
      .catch((e) => {
        if (!cancelled) {
          setCalendarStatusCounts(createEmptyCalendarStatusCounts());
          setCalendarError(e instanceof Error ? e.message : 'Failed to load calendar');
        }
      })
      .finally(() => {
        if (!cancelled) setCalendarLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [inTeam, calendarDate, calendarMonth, selectedTeamId, isTeamCreator, scheduleTick]);

  useEffect(() => {
    if (!inTeam || !selectedTeamId) return;

    let cancelled = false;
    setDayOrdersLoading(true);
    setDayOrdersError(null);
    fetchDailyOrders({
      date: calendarDate,
      asTeamAdmin: isTeamCreator,
      groupByField: 'service_time',
    })
      .then((result) => {
        if (cancelled) return;
        setDayOrders(result.orders);
      })
      .catch((e) => {
        if (!cancelled) {
          setDayOrders([]);
          setDayOrdersError(e instanceof Error ? e.message : 'Failed to load orders');
        }
      })
      .finally(() => {
        if (!cancelled) setDayOrdersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [inTeam, selectedTeamId, calendarDate, isTeamCreator, scheduleTick]);

  const handleMonthChange = useCallback((month: string) => {
    setCalendarMonth(month);
  }, []);

  const currentUserId = profile ? String(profile.userID) : null;

  const patchDayOrder = useCallback((orderId: string, patch: (order: Order) => Order) => {
    setDayOrders((prev) => prev.map((order) => (order.id === orderId ? patch(order) : order)));
  }, []);

  const handleConfirmAssign = useCallback(async (
    operatorIds: string[] | undefined,
    billing: OrderBillingValues,
    staff: OrderServicerInfo[],
  ) => {
    if (!assignOrder || assignSubmitting) return;
    if (assignMode === 'assign' && (!operatorIds || operatorIds.length === 0)) return;
    setAssignSubmitting(true);
    try {
      const result = assignMode === 'assign'
        ? await postAssignOrder({
            orderId: assignOrder.id,
            operatorIds: operatorIds ?? [],
            serviceDuration: billing.serviceDuration,
          })
        : await postOrderBillingAdjustment(assignOrder, billing, operatorIds);
      patchDayOrder(assignOrder.id, (order) => (
        assignMode === 'assign'
          ? patchOrderAfterAssignment(
              { ...order, serviceDuration: billing.serviceDuration },
              operatorIds ?? [],
              undefined,
              staff,
            )
          : patchOrderAfterOperation(order, operatorIds, billing, staff)
      ));
      setAssignOrder(null);
      setScheduleTick((n) => n + 1);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update order.',
        success: false,
      });
    } finally {
      setAssignSubmitting(false);
    }
  }, [assignMode, assignOrder, assignSubmitting, patchDayOrder]);

  const handleCompleteService = useCallback(async (target: Order) => {
    if (completingOrderId) return;
    setCompletingOrderId(target.id);
    try {
      const result = await postMarkServiceCompleted({ orderId: target.id });
      const allCompleted = isAssignedServiceFullyCompleted(target, result.allCompleted);
      patchDayOrder(target.id, (order) =>
        patchOrderAfterServiceCompleted(order, allCompleted),
      );
      setScheduleTick((n) => n + 1);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to mark service completed.',
        success: false,
      });
    } finally {
      setCompletingOrderId(null);
    }
  }, [completingOrderId, patchDayOrder]);

  const handleConfirmReorder = useCallback(async (payload: ReorderOrderConfirmPayload) => {
    if (!reorderOrder || reorderSubmitting) return;
    setReorderSubmitting(true);
    try {
      const result = await postAdminReorder({
        orderId: reorderOrder.id,
        date: payload.date,
        time: payload.time,
        otherInfo: payload.otherInfo,
      });
      setReorderOrder(null);
      showApiResultModal({ message: result.message, success: true });
      navigate('/team', { replace: true, state: { focusDate: payload.date } });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to reorder.',
        success: false,
      });
    } finally {
      setReorderSubmitting(false);
    }
  }, [navigate, reorderOrder, reorderSubmitting]);

  const markInvoiceSent = useCallback((order: Order) => {
    rememberInvoiceSent(order.id);
    patchDayOrder(order.id, (current) => ({ ...current, invoiceSent: true }));
  }, [patchDayOrder]);

  const handleConfirmPaymentClose = useCallback(async (
    target: Order,
    details?: { paymentMethod?: 'transfer' | 'cash'; paymentDate?: string },
  ) => {
    if (paymentSubmitting) return;
    const action = resolveInvoiceCloseAction(target);
    if (!action) return;
    setPaymentSubmitting(true);
    try {
      const result = await postConfirmInvoicePaymentAndClose(target, action, details);
      patchDayOrder(target.id, (order) =>
        patchOrderAfterStatusChange(
          { ...order, payStatus: result.payStatus, invoiceSent: true },
          result.closed ? 'completed' : order.status,
          result.closed ? 3 : (order.rawStatus ?? 2),
        ),
      );
      setPaymentOrder(null);
      setScheduleTick((n) => n + 1);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      if (e instanceof OrderClosedBeforePaymentError) {
        patchDayOrder(target.id, (order) =>
          patchOrderAfterStatusChange(
            { ...order, payStatus: 0, invoiceSent: true },
            'completed',
            3,
          ),
        );
        setPaymentOrder(null);
        setScheduleTick((n) => n + 1);
      } else if (e instanceof OrderCloseAfterPaymentError) {
        patchDayOrder(target.id, (order) => ({ ...order, payStatus: 1, invoiceSent: true }));
        setPaymentOrder((current) => (current && current.id === target.id ? { ...current, payStatus: 1, invoiceSent: true } : current));
      }
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to confirm payment.',
        success: false,
      });
    } finally {
      setPaymentSubmitting(false);
    }
  }, [paymentSubmitting, patchDayOrder]);

  const handleOnlineStatusChange = async (nextOnline: boolean) => {
    if (onlineStatusUpdating) return;

    const previousOnline = isOnline;
    setIsOnline(nextOnline);
    setOnlineStatusUpdating(true);
    try {
      const result = await postSetOnlineStatus(nextOnline ? 1 : 0);
      setIsOnline(result.online === 1);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      setIsOnline(previousOnline);
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update online status.',
        success: false,
      });
    } finally {
      setOnlineStatusUpdating(false);
    }
  };

  const { createdTeams, joinedTeams } = useMemo(() => {
    const createdTeamId = profile ? readTeamInfoId(profile.team_info) : null;
    const isCreatedTeam = (team: UserTeamMembership) =>
      createdTeamId != null ? String(team.teamId) === createdTeamId : team.role === 1;
    const created: UserTeamMembership[] = [];
    const joined: UserTeamMembership[] = [];
    for (const team of userTeams) {
      if (isCreatedTeam(team)) created.push(team);
      else joined.push(team);
    }
    return { createdTeams: created, joinedTeams: joined };
  }, [userTeams, profile]);

  const canCreateNewOrder = createdTeams.length > 0;

  useEffect(() => {
    if (createdTeams.length === 0) {
      setCreatedTeamTaxLabel(null);
      setCreatedTeamId(null);
      return;
    }
    let cancelled = false;
    fetchAdminTeamInfo()
      .then((team) => {
        if (cancelled) return;
        setCreatedTeamId(String(team.id));
        setCreatedTeamTaxLabel(formatInvoiceTaxLabel(team.invoice_info));
      })
      .catch(() => {
        if (!cancelled) {
          setCreatedTeamTaxLabel(null);
          setCreatedTeamId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [createdTeams.length]);

  const orderedDayOrders = useMemo(
    () =>
      [...dayOrders].sort((a, b) =>
        String(a.service_time).localeCompare(String(b.service_time)),
      ),
    [dayOrders],
  );

  const selectedDayCounts = dayStatusMap[calendarDate] ?? calendarStatusCounts;
  const statusBadges = activeCalendarStatuses(selectedDayCounts);
  const showStatusBadges = hasCalendarStatusCounts(selectedDayCounts);
  const selectedDayAmounts = isTeamCreator ? dayAmounts[calendarDate] : undefined;

  return (
    <div className="ui-tab-page bg-[#f8f9fa]">
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 pt-12 pb-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-gray-900 text-lg font-bold leading-tight">Team</h1>
            <p className="text-gray-400 text-xs">Schedule and team info</p>
          </div>
          {inTeam ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`text-xs font-semibold whitespace-nowrap ${
                  isOnline ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {isOnline ? 'Online' : 'Offline'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isOnline}
                aria-label={isOnline ? 'Switch to offline' : 'Switch to online'}
                disabled={onlineStatusUpdating}
                onClick={() => void handleOnlineStatusChange(!isOnline)}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
                  isOnline ? 'bg-green-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    isOnline ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-4 pt-3 space-y-2.5">
        {teamInfoLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
            <p className="text-gray-400 text-xs mt-1.5">Loading team info...</p>
          </div>
        ) : needsTeamPrompt(profile) && userTeams.length === 0 ? (
          <NoTeamPrompt profile={profile} />
        ) : !selectedTeam ? (
          <NoTeamPrompt profile={profile} />
        ) : (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-gray-700 text-xs flex items-center gap-1.5 mb-2.5">
                <i className="ri-calendar-schedule-line text-green-500" />
                My Work Order Schedule
              </div>

              <div className="mb-2.5">
                <StaffScheduleCalendar
                  value={calendarDate}
                  onChange={setCalendarDate}
                  dayStatusMap={dayStatusMap}
                  onMonthChange={handleMonthChange}
                />
              </div>
              {calendarLoading ? (
                <div className="text-center py-5">
                  <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
                  <p className="text-gray-400 text-xs mt-1.5">Loading schedule...</p>
                </div>
              ) : calendarError ? (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {calendarError}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {monthAmounts ? (
                    <div>
                      <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">
                        This month
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <CalendarAmountStat
                          label="Completed Ex-GST"
                          value={formatOrderTotalPrice(monthAmounts.amountCompleted)}
                        />
                        <CalendarAmountStat
                          label="Completed Inc-GST"
                          value={formatOrderTotalPrice(monthAmounts.amountCompletedWithGst)}
                        />
                      </div>
                    </div>
                  ) : null}
                  {selectedDayAmounts ? (
                    <div>
                      <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">
                        This day
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <CalendarAmountStat
                          label="Completed Ex-GST"
                          value={formatOrderTotalPrice(selectedDayAmounts.amountCompleted)}
                        />
                        <CalendarAmountStat
                          label="Completed Inc-GST"
                          value={formatOrderTotalPrice(selectedDayAmounts.amountCompletedWithGst)}
                        />
                      </div>
                    </div>
                  ) : null}
                  {showStatusBadges ? (
                    <div className="flex flex-wrap gap-1.5">
                      {statusBadges.map(({ key, count }) => {
                        const meta = CALENDAR_STATUS_META[key];
                        return (
                          <span
                            key={key}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-normal ${meta.badgeClass}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`} />
                            {meta.label}
                            <span className="font-semibold tabular-nums">{count}</span>
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 text-xs py-4">No jobs on this day</p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
                <span className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">
                  Day Orders
                </span>
                <span className="text-[11px] text-gray-400 truncate">
                  {formatDayLabel(calendarDate)}
                </span>
              </div>
              {dayOrdersLoading ? (
                <div className="text-center py-6">
                  <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
                  <p className="text-gray-400 text-xs mt-1.5">Loading orders...</p>
                </div>
              ) : dayOrdersError ? (
                <div className="m-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {dayOrdersError}
                </div>
              ) : orderedDayOrders.length === 0 ? (
                <p className="text-center text-gray-400 text-xs py-6">No orders on this day</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {orderedDayOrders.map((order) => {
                    const cfg = getOrderStatusMeta(order.status);
                    const isOrderTeamAdmin = canViewOrderTeamPrivacy(
                      profile,
                      order.teamId,
                      order.teamName,
                    );
                    const showAssign = canTeamAdminEditWorkHours(order, isOrderTeamAdmin) && !orderHasAssignedStaff(order);
                    const showOperation = canTeamAdminEditOrderBeforeComplete(order, isOrderTeamAdmin);
                    const showComplete = canUserMarkServiceCompleted(order, currentUserId);
                    const showInvoice = isOrderTeamAdmin && (order.status === 'service_done' || order.status === 'completed');
                    const showRepeat = isOrderTeamAdmin && order.status === 'completed';
                    const showReorder = isOrderTeamAdmin;
                    const paymentAction = isOrderTeamAdmin ? resolveInvoiceCloseAction(order) : null;
                    const paymentBusy = paymentSubmitting && paymentOrder?.id === order.id;
                    const customerContact = [order.customer.phone, order.customer.email]
                      .filter((v) => v && v !== '—')
                      .join(' · ');
                    return (
                      <div key={order.id} className="px-3 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <i className="ri-team-line text-sm text-gray-300 flex-shrink-0" aria-hidden />
                            <span className="text-xs font-medium text-gray-800 truncate min-w-0">
                              {order.teamName?.trim() || 'Team'}
                            </span>
                            {order.id.trim() ? (
                              <span className="text-[11px] font-medium text-gray-400 flex-shrink-0 tabular-nums">
                                {order.id.trim().startsWith('#') ? order.id.trim() : `#${order.id.trim()}`}
                              </span>
                            ) : null}
                          </div>
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 ${cfg.badgeClass}`}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        {isOrderTeamAdmin ? (
                          <div className="flex items-center gap-2 mt-1 min-w-0">
                            <i className="ri-user-3-line text-sm text-gray-300 flex-shrink-0" aria-hidden />
                            <span className="text-xs font-semibold text-gray-800 truncate min-w-0">
                              {order.customer.name}
                            </span>
                            {customerContact ? (
                              <span className="text-[11px] text-gray-400 truncate min-w-0">
                                {customerContact}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="flex items-center gap-1.5 mt-1 min-w-0">
                          <i className="ri-time-line text-gray-300 text-xs flex-shrink-0" />
                          <FormattedServiceTime
                            value={order.service_time}
                            className="text-[11px] text-gray-500 truncate"
                          />
                        </div>
                        {order.customer.address ? (
                          <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                            <i className="ri-map-pin-2-line text-gray-300 text-xs flex-shrink-0" />
                            <span className="text-[11px] text-gray-400 truncate">
                              {order.customer.address}
                            </span>
                          </div>
                        ) : null}
                        {isOrderTeamAdmin ? <AdminOrderBillingRow order={order} /> : null}
                        <div className="mt-2 grid grid-cols-2 gap-1.5">
                          {([
                            {
                              key: 'detail',
                              label: 'Detail',
                              icon: 'ri-file-list-3-line',
                              onClick: () => navigate(`/orders/${encodeURIComponent(order.id)}`, { state: { from: '/team' } }),
                            },
                            showAssign ? {
                              key: 'assign',
                              label: 'Assign',
                              icon: 'ri-user-add-line',
                              onClick: () => {
                                setAssignMode('assign');
                                setAssignOrder(order);
                              },
                            } : null,
                            showOperation ? {
                              key: 'operation',
                              label: 'Price & Hours',
                              icon: 'ri-price-tag-3-line',
                              onClick: () => {
                                setAssignMode('billing');
                                setAssignOrder(order);
                              },
                            } : null,
                            showComplete ? {
                              key: 'complete',
                              label: completingOrderId === order.id ? 'Saving…' : 'Complete',
                              icon: completingOrderId === order.id ? 'ri-loader-4-line animate-spin' : 'ri-checkbox-circle-line',
                              disabled: completingOrderId === order.id,
                              onClick: () => void handleCompleteService(order),
                            } : null,
                            showInvoice ? {
                              key: 'invoice',
                              label: 'Invoice',
                              icon: 'ri-bill-line',
                              onClick: () => setInvoiceOrder(order),
                            } : null,
                            showRepeat ? {
                              key: 'repeat',
                              label: 'Repeat',
                              icon: 'ri-repeat-line',
                              onClick: () => setRecurringOrder(order),
                            } : null,
                            showReorder ? {
                              key: 'reorder',
                              label: 'Reorder',
                              icon: 'ri-file-copy-line',
                              onClick: () => setReorderOrder(order),
                            } : null,
                            paymentAction ? {
                              key: 'close',
                              label: paymentBusy ? 'Saving…' : orderListPaymentLabel(invoiceCloseActionLabel(paymentAction)),
                              icon: paymentBusy ? 'ri-loader-4-line animate-spin' : 'ri-secure-payment-line',
                              disabled: paymentBusy,
                              onClick: () => setPaymentOrder(order),
                            } : null,
                          ].filter((action) => action !== null)).map((action, index, actions) => (
                            <button
                              key={action.key}
                              type="button"
                              disabled={action.disabled}
                              onClick={action.onClick}
                              className={`${ORDER_LIST_ACTION_CLASS} justify-center min-w-0 ${
                                index === actions.length - 1 && actions.length % 2 === 1 ? 'col-span-2' : ''
                              }`}
                            >
                              <i className={`${action.icon} text-xs`} />
                              <span className="truncate">{action.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <TeamGroupCard
              title="My Created Team"
              teams={createdTeams}
              describeTeam={(team) => (
                createdTeamId != null && String(team.teamId) === createdTeamId
                  ? createdTeamTaxLabel
                  : null
              )}
              action={
                canCreateNewOrder ? (
                  <button
                    type="button"
                    onClick={() => navigate('/orders/new', { state: { from: '/team' } })}
                    className="h-7 px-2 rounded-md bg-green-600 text-white text-[11px] font-normal cursor-pointer hover:bg-green-700 flex items-center gap-0.5 flex-shrink-0"
                  >
                    <i className="ri-add-line text-xs" />
                    Create New Order
                  </button>
                ) : null
              }
              footer={
                <button
                  type="button"
                  onClick={() => navigate('/team/orders')}
                  className="w-full px-3 py-2.5 flex items-center gap-2 cursor-pointer text-left hover:bg-gray-50"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 flex-shrink-0">
                    <i className="ri-file-list-3-line text-gray-500 text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 text-xs font-medium">Team All Orders</div>
                    <div className="text-gray-400 text-[11px] mt-0.5">All orders for this team</div>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300 text-base flex-shrink-0" />
                </button>
              }
            />
            <TeamGroupCard
              title="Joined Teams"
              teams={joinedTeams}
              onOpenInfo={(team) => navigate(`/team/info/${encodeURIComponent(String(team.teamId))}`)}
            />
            {canJoinAnotherTeam(profile) ? (
              <button
                type="button"
                onClick={() => navigate('/me/team/join')}
                className="w-full bg-white rounded-lg border border-gray-200 px-3 py-2.5 flex items-center gap-2 cursor-pointer text-left hover:bg-gray-50"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 flex-shrink-0">
                  <i className="ri-login-circle-line text-gray-500 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-800 text-xs font-medium">Join Another Team</div>
                  <div className="text-gray-400 text-[11px] mt-0.5">Use a team code to join</div>
                </div>
                <i className="ri-arrow-right-s-line text-gray-300 text-base flex-shrink-0" />
              </button>
            ) : null}
          </>
        )}
      </div>

      <AssignOrderModal
        order={assignOrder}
        mode={assignMode}
        submitting={assignSubmitting}
        onClose={() => {
          if (!assignSubmitting) setAssignOrder(null);
        }}
        onConfirm={(ids, billing, staff) => void handleConfirmAssign(ids, billing, staff)}
      />

      <InvoiceOrderModal
        order={invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        onInvoiceSent={markInvoiceSent}
        onConfirmPayment={(sent) => {
          markInvoiceSent(sent);
          setInvoiceOrder(null);
          setPaymentOrder({ ...sent, invoiceSent: true });
        }}
      />

      <ConfirmPaymentCloseModal
        order={paymentOrder}
        action={paymentOrder ? resolveInvoiceCloseAction(paymentOrder) : null}
        submitting={paymentSubmitting}
        onClose={() => {
          if (!paymentSubmitting) setPaymentOrder(null);
        }}
        onConfirm={(details) => {
          if (!paymentOrder) return;
          void handleConfirmPaymentClose(paymentOrder, details);
        }}
      />

      <CreateRecurringOrderModal
        order={recurringOrder}
        onClose={() => setRecurringOrder(null)}
      />

      <ReorderOrderModal
        order={reorderOrder}
        submitting={reorderSubmitting}
        onClose={() => {
          if (!reorderSubmitting) setReorderOrder(null);
        }}
        onConfirm={(payload) => void handleConfirmReorder(payload)}
      />

    </div>
  );
}

function formatOrderGstLabel(gstRate: number | undefined): string {
  const gstDecimal = orderGstRateToDecimal(gstRate);
  if (gstDecimal === null) return '—';
  return `${formatGstPercentInput(gstDecimal)}%`;
}

function AdminOrderBillingRow({ order }: { order: Order }) {
  const fee = typeof order.totalPriceWithGst === 'number'
    ? order.totalPriceWithGst
    : order.totalPrice;

  return (
    <div className="mt-1.5 grid grid-cols-3 gap-1">
      <AdminBillingStat label="GST" value={formatOrderGstLabel(order.gstRate)} />
      <AdminBillingStat label="Hours" value={formatOrderDurationHours(order.serviceDuration)} />
      <AdminBillingStat label="Fee" value={formatOrderTotalPrice(fee)} />
    </div>
  );
}

function CalendarAmountStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-gray-50 border border-gray-100 px-2 py-1.5 min-w-0">
      <div className="text-[10px] text-gray-400 leading-none">{label}</div>
      <div className="mt-1 text-xs font-semibold text-gray-800 tabular-nums truncate">{value}</div>
    </div>
  );
}

function AdminBillingStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-gray-50 border border-gray-100 px-1.5 py-1 min-w-0 text-center">
      <div className="text-[10px] text-gray-400 leading-none">{label}</div>
      <div className="mt-0.5 text-[11px] font-semibold text-gray-700 tabular-nums truncate">
        {value}
      </div>
    </div>
  );
}

function TeamGroupCard({
  title,
  teams,
  action,
  onOpenInfo,
  describeTeam,
  footer,
}: {
  title: string;
  teams: UserTeamMembership[];
  action?: ReactNode;
  onOpenInfo?: (team: UserTeamMembership) => void;
  describeTeam?: (team: UserTeamMembership) => string | null;
  footer?: ReactNode;
}) {
  if (teams.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2">
        <span className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">
          {title}
        </span>
        {action}
      </div>
      <div className="divide-y divide-gray-100">
        {teams.map((team) => {
          const contactLine = [team.phone, team.email].filter(Boolean).join(' · ');
          const taxLabel = describeTeam?.(team);
          const inner = (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-800 truncate">
                  {team.teamName}
                </div>
                <div className="text-[11px] text-gray-400 truncate mt-0.5">
                  {contactLine || 'No phone or email'}
                </div>
                {taxLabel ? (
                  <div className="text-[11px] text-gray-500 truncate mt-0.5">{taxLabel}</div>
                ) : null}
              </div>
              {team.role === 1 ? (
                <span className="text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  Founder
                </span>
              ) : (
                <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  Member
                </span>
              )}
              {onOpenInfo ? (
                <i className="ri-arrow-right-s-line text-gray-300 text-lg flex-shrink-0" aria-hidden />
              ) : null}
            </>
          );
          if (onOpenInfo) {
            return (
              <button
                key={String(team.teamId)}
                type="button"
                onClick={() => onOpenInfo(team)}
                className="w-full px-3 py-2.5 flex items-center gap-2 cursor-pointer text-left hover:bg-gray-50"
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={String(team.teamId)}
              className="w-full px-3 py-2.5 flex items-center gap-2"
            >
              {inner}
            </div>
          );
        })}
      </div>
      {footer ? <div className="border-t border-gray-100">{footer}</div> : null}
    </div>
  );
}
